// Drawing on the board: pen strokes, boxes, circles, arrows and handwritten
// labels. Each is an item with a box, so it moves, resizes and undoes like a widget.

import { getStroke } from "perfect-freehand";
import { memo } from "react";
import type { InkItem, InkShape } from "../../shared/types";
import { contrast } from "../themes/themes";

export const inkColor = (c: string) => (c === "auto" ? "var(--fg)" : c);

/** Whether a label's own color is light or dark, for the shade it keeps over a photograph. The theme's own ink goes by the theme. */
const inkTone = (c: string): "light" | "dark" | undefined => (c === "auto" ? undefined : contrast(c, "#000000") >= contrast(c, "#ffffff") ? "light" : "dark");

export function strokePath(pts: number[], size: number, simulate = true): string {
  const input: [number, number, number][] = [];
  for (let i = 0; i + 2 < pts.length; i += 3) input.push([pts[i], pts[i + 1], pts[i + 2]]);
  const outline = getStroke(input, { size, thinning: 0.55, smoothing: 0.6, streamline: 0.45, simulatePressure: simulate, last: true });
  if (!outline.length) return "";
  const d: (string | number)[] = ["M", outline[0][0].toFixed(1), outline[0][1].toFixed(1), "Q"];
  for (let i = 0; i < outline.length; i++) {
    const [x0, y0] = outline[i];
    const [x1, y1] = outline[(i + 1) % outline.length];
    d.push(x0.toFixed(1), y0.toFixed(1), ((x0 + x1) / 2).toFixed(1), ((y0 + y1) / 2).toFixed(1));
  }
  d.push("Z");
  return d.join(" ");
}

export const HAND_FONTS: Record<Extract<InkShape, { t: "text" }>["font"], string> = {
  hand: "'Caveat', 'Comic Sans MS', cursive",
  sans: "'Geist Variable', system-ui, sans-serif",
  serif: "'Instrument Serif', Georgia, serif",
  mono: "'JetBrains Mono Variable', monospace",
};

function Arrowhead({ x1, y1, x2, y2, size, color }: { x1: number; y1: number; x2: number; y2: number; size: number; color: string }) {
  const ang = Math.atan2(y2 - y1, x2 - x1);
  const len = Math.max(10, size * 4.2);
  const spread = 0.48;
  const a = { x: x2 - len * Math.cos(ang - spread), y: y2 - len * Math.sin(ang - spread) };
  const b = { x: x2 - len * Math.cos(ang + spread), y: y2 - len * Math.sin(ang + spread) };
  return <path d={`M ${a.x} ${a.y} L ${x2} ${y2} L ${b.x} ${b.y}`} fill="none" style={{ stroke: color }} strokeWidth={size} strokeLinecap="round" strokeLinejoin="round" />;
}

export function InkShapeSvg({ ink, w, h }: { ink: InkShape; w: number; h: number }) {
  switch (ink.t) {
    case "path": {
      const d = strokePath(ink.pts, ink.size);
      return (
        <svg className="ink-svg" width={w} height={h} viewBox={`0 0 ${ink.vw || w} ${ink.vh || h}`} preserveAspectRatio="none" style={{ opacity: ink.opacity }}>
          <path d={d} style={{ fill: inkColor(ink.color) }} />
        </svg>
      );
    }
    case "rect":
    case "ellipse": {
      const sw = ink.size;
      const dash = ink.dash ? `${sw * 2.4} ${sw * 2.2}` : undefined;
      return (
        <svg className="ink-svg" width={w} height={h} style={{ opacity: ink.opacity, overflow: "visible" }}>
          {ink.t === "rect" ? (
            <rect x={sw / 2} y={sw / 2} width={Math.max(0, w - sw)} height={Math.max(0, h - sw)} rx={Math.min(14, w / 6, h / 6)} fill={ink.fill ?? "none"} style={{ stroke: inkColor(ink.color) }} strokeWidth={sw} strokeDasharray={dash} strokeLinejoin="round" />
          ) : (
            <ellipse cx={w / 2} cy={h / 2} rx={Math.max(0, w / 2 - sw / 2)} ry={Math.max(0, h / 2 - sw / 2)} fill={ink.fill ?? "none"} style={{ stroke: inkColor(ink.color) }} strokeWidth={sw} strokeDasharray={dash} />
          )}
        </svg>
      );
    }
    case "arrow":
    case "line": {
      const x1 = ink.x1 * w;
      const y1 = ink.y1 * h;
      const x2 = ink.x2 * w;
      const y2 = ink.y2 * h;
      return (
        <svg className="ink-svg" width={w} height={h} style={{ opacity: ink.opacity, overflow: "visible" }}>
          <line x1={x1} y1={y1} x2={x2} y2={y2} style={{ stroke: inkColor(ink.color) }} strokeWidth={ink.size} strokeLinecap="round" strokeDasharray={ink.dash ? `${ink.size * 2.4} ${ink.size * 2.2}` : undefined} />
          {ink.t === "arrow" && <Arrowhead x1={x1} y1={y1} x2={x2} y2={y2} size={ink.size} color={inkColor(ink.color)} />}
        </svg>
      );
    }
    case "text":
      return (
        <div className="ink-text" data-ink={inkTone(ink.color)} style={{ color: inkColor(ink.color), fontSize: ink.fontSize, fontFamily: HAND_FONTS[ink.font], opacity: ink.opacity }}>
          {ink.text || " "}
        </div>
      );
  }
}

export const InkView = memo(function InkView({ item, editing, onText }: { item: InkItem; editing: boolean; onText?: (text: string) => void }) {
  if (editing && item.ink.t === "text") {
    const ink = item.ink;
    return (
      <textarea
        className="ink-text ink-text-edit"
        data-ink={inkTone(ink.color)}
        autoFocus
        defaultValue={ink.text}
        style={{ color: inkColor(ink.color), fontSize: ink.fontSize, fontFamily: HAND_FONTS[ink.font] }}
        onFocus={(e) => e.currentTarget.select()}
        onBlur={(e) => onText?.(e.currentTarget.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape" || (e.key === "Enter" && !e.shiftKey)) {
            e.preventDefault();
            (e.currentTarget as HTMLTextAreaElement).blur();
          }
          e.stopPropagation();
        }}
        onPointerDown={(e) => e.stopPropagation()}
      />
    );
  }
  return <InkShapeSvg ink={item.ink} w={item.w} h={item.h} />;
});

/** Size a text label to its words. */
export function measureText(text: string, font: Extract<InkShape, { t: "text" }>["font"], fontSize: number): { w: number; h: number } {
  const c = document.createElement("canvas").getContext("2d")!;
  c.font = `${font === "hand" ? 500 : 400} ${fontSize}px ${HAND_FONTS[font]}`;
  const lines = (text || " ").split("\n");
  const w = Math.max(...lines.map((l) => c.measureText(l || " ").width));
  return { w: Math.ceil(w + fontSize * 0.6), h: Math.ceil(lines.length * fontSize * 1.25 + fontSize * 0.3) };
}
